import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

/**
 * Simple production-safe error boundary.
 *
 * Wrap a route subtree with this so a runtime error (e.g. a missing import,
 * undefined-access, map() on undefined) shows a friendly fallback UI instead
 * of leaving the user with a blank white screen.
 *
 * Usage:
 *   <ErrorBoundary scope="partner">
 *     <Route ... />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Only log in development so production users don't see noise.
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(`[ErrorBoundary:${this.props.scope || 'app'}]`, error, info?.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleHome = () => {
    window.location.href = this.props.homePath || '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const scopeLabel = this.props.scope === 'partner' ? 'Partner app'
      : this.props.scope === 'customer' ? 'Customer app'
      : this.props.scope === 'admin' ? 'Admin dashboard'
      : 'BeautyMateUK';

    return (
      <div
        data-testid={`error-boundary-${this.props.scope || 'app'}`}
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12"
      >
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 mb-4">
            <AlertCircle className="h-6 w-6 text-rose-600" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-1">
            Something went wrong loading the {scopeLabel}.
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            We hit an unexpected error rendering this page. Please try again,
            or head back to the homepage.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="error-boundary-retry"
              className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
            >
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors"
            >
              <Home className="h-4 w-4" /> Go to homepage
            </button>
          </div>
        </div>
      </div>
    );
  }
}
